import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useToast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const ProductRow = ({ product, onSaved }) => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    originalPrice: product.originalPrice,
    salePrice: product.salePrice,
    stock: product.stock ?? '',
    onSale: product.onSale,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        originalPrice: Number(form.originalPrice),
        salePrice: Number(form.salePrice),
        onSale: form.onSale,
      };
      if (form.stock !== '') payload.stock = Number(form.stock);
      await axios.patch(`${API}/api/admin/products/${product.slug}`, payload, { withCredentials: true });
      toast({ title: `${product.name} updated` });
      onSaved();
    } catch {
      toast({ title: `Failed to update ${product.name}`, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const inputClass = 'w-24 border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500';

  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50" data-testid={`admin-product-row-${product.slug}`}>
      <td className="py-3 pr-4">
        <div className="flex items-center gap-3">
          <img src={product.image} alt={product.name} className="w-10 h-10 rounded-lg object-cover bg-gray-100" />
          <div>
            <p className="text-gray-900 font-medium">{product.name}</p>
            <p className="text-gray-400 text-xs">{product.category}</p>
          </div>
        </div>
      </td>
      <td className="py-3 pr-4">
        <input type="number" value={form.originalPrice} data-testid={`admin-price-input-${product.slug}`}
          onChange={(e) => setForm({ ...form, originalPrice: e.target.value })} className={inputClass} />
      </td>
      <td className="py-3 pr-4">
        <input type="number" value={form.salePrice} data-testid={`admin-saleprice-input-${product.slug}`}
          onChange={(e) => setForm({ ...form, salePrice: e.target.value })} className={inputClass} />
      </td>
      <td className="py-3 pr-4">
        <input type="number" min="0" value={form.stock} placeholder="∞" data-testid={`admin-stock-input-${product.slug}`}
          onChange={(e) => setForm({ ...form, stock: e.target.value })} className="w-20 border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
      </td>
      <td className="py-3 pr-4">
        <input type="checkbox" checked={form.onSale} data-testid={`admin-onsale-toggle-${product.slug}`}
          onChange={(e) => setForm({ ...form, onSale: e.target.checked })} className="w-4 h-4 accent-emerald-600" />
      </td>
      <td className="py-3">
        <button onClick={save} disabled={saving} data-testid={`admin-save-product-${product.slug}`}
          className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-60 text-white px-4 py-1.5 rounded-lg text-sm font-medium transition-all">
          {saving ? 'Saving…' : 'Save'}
        </button>
      </td>
    </tr>
  );
};

export const AdminProducts = () => {
  const [products, setProducts] = useState(null);

  const load = () => {
    axios.get(`${API}/api/admin/products`, { withCredentials: true })
      .then(({ data }) => setProducts(data))
      .catch(() => setProducts([]));
  };

  useEffect(load, []);

  if (products === null) return <p className="text-gray-500 py-8">Loading products…</p>;

  return (
    <div className="overflow-x-auto" data-testid="admin-products-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-gray-500 border-b border-gray-200">
            <th className="py-3 pr-4 font-medium">Product</th>
            <th className="py-3 pr-4 font-medium">MRP (₹)</th>
            <th className="py-3 pr-4 font-medium">Sale Price (₹)</th>
            <th className="py-3 pr-4 font-medium">Stock</th>
            <th className="py-3 pr-4 font-medium">On Sale</th>
            <th className="py-3 font-medium">Action</th>
          </tr>
        </thead>
        <tbody>
          {products.map((p) => <ProductRow key={p.slug} product={p} onSaved={load} />)}
        </tbody>
      </table>
    </div>
  );
};
